import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/system';
import { MapRef } from 'react-map-gl';
import { useSnapshot } from 'valtio';

import { Backdrop, CircularProgress } from '@mui/material';

import { appStore } from '@/store';

import { AssetSearchDialog } from '../EmissionsDashboard/AssetSearchDialog';

import { ChatWindow } from './components/AiAgent/ChatWindow';
import LineChart from './components/Chart/LineChart';
import { Context } from './components/Context';
import { EventTimeline } from './components/EventTable';
import { InteractiveMap } from './components/Mapbox';
import { useFetchData } from './hooks/useFetchData';
import { useFilterData } from './hooks/useFilterData';
import { setConversationId, setReeferId, store } from './store';

import 'mapbox-gl/dist/mapbox-gl.css';
import 'mapbox-gl/dist/mapbox-gl.css';

export type Coordinates = [longitude: number, latitude: number];
export type TimestampRange = [startTs: number, endTs: number];
export type Serie = number[];

function ReeferInsights() {
  /**========================================================================
   *                           STATE & VARIABLES
   *========================================================================**/
  const { isLoading } = useSnapshot(store);
  const appSnap = useSnapshot(appStore);

  const mapRef = useRef<MapRef>(null);
  const [open, setOpen] = useState(false);
  const [linechartContainerHeight, setLinechartContainerHeight] = useState<
    number | undefined
  >();
  const [chatOpen, setChatOpen] = useState(false);
  const { tsFilterRange, reeferId } = useSnapshot(store);

  useFetchData();

  const ref = useCallback((node: any) => {
    if (node !== null) {
      setLinechartContainerHeight(node.getBoundingClientRect().height);
    }
  }, []);
  useFilterData(mapRef);

  useEffect(() => {
    setConversationId();
  }, []);

  const ChatButton = ({ onClick }: { onClick: () => void }) => {
    if (!tsFilterRange) {
      return null;
    }
    if (!reeferId) {
      return null;
    }
    return (
      <button
        onClick={onClick}
        style={{
          position: 'fixed',
          bottom: '100px',
          right: '20px',
          zIndex: 1300,
          backgroundColor: '#1976d2',
          color: 'white',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          border: 'none',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          fontSize: '24px',
          cursor: 'pointer',
        }}
      >
        💬
      </button>
    );
  };

  return (
    <Box
      sx={{
        ...styles.container,
        height: `calc(100vh - ${appSnap.appBar.height}px)`,
      }}
    >
      {/* Modal de recherche */}
      <AssetSearchDialog
        open={open}
        onClose={() => setOpen(false)}
        onSelectAsset={(reefer) => {
          setReeferId(reefer.id);
          setOpen(false);
        }}
      />

      {/* Backdrop de chargement */}
      {isLoading && (
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            pointerEvents: 'none',
          }}
          open={isLoading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
      )}

      <Box sx={styles.leftColumn}>
        <Box sx={styles.filtersContainer}>
          <Context setOpen={setOpen} />
        </Box>
        <Box sx={styles.linechartContainer} ref={ref}>
          <LineChart mapRef={mapRef} height={linechartContainerHeight} />
        </Box>
      </Box>
      <Box sx={styles.rightColumn}>
        <Box sx={styles.mapContainer}>
          <InteractiveMap mapRef={mapRef} />
        </Box>
        <Box sx={styles.eventTableContainer}>
          <EventTimeline />
        </Box>
      </Box>

      {/* Bouton et fenêtre de chat */}
      {!chatOpen && <ChatButton onClick={() => setChatOpen(true)} />}
      {chatOpen && <ChatWindow onClose={() => setChatOpen(false)} />}
    </Box>
  );
}

const styles = {
  container: {
    display: 'flex',
    width: '100%',
    gap: '20px',
    // paddingHorizontal: '20px',
  },
  leftColumn: {
    display: 'flex',
    padding: '20px',
    flex: 1,
    height: '100%',
    flexDirection: 'column',
    gap: '20px',
    borderRadius: '8px',
  },
  rightColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    borderRadius: '8px',
    padding: '20px',
  },
  filtersContainer: {
    flex: 1,
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  },
  linechartContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  },
  mapContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  },
  eventTableContainer: {
    backgroundColor: 'white',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    padding: '20px',
  },
};

export { ReeferInsights };
